import { formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import {
  adminApi,
  type CreatePlatformSmsDispatchMessageRequest,
  type CreateSmsGatewayRequest,
  type SmsDispatchMessage,
  type SmsGateway,
  type SmsGatewayScopeType,
  type Tenant,
  type UpdateSmsGatewayRequest,
} from '../../services/adminApi';

const DEFAULT_REGION = 'Australia East';
const DISPATCH_PAGE_SIZE = 25;
const DEFAULT_SIGNALR_NEGOTIATE_PATH = '/api/signalr/negotiate';
const AUTO_REFRESH_MS = 15000;

function getDispatchStatusTone(status: string): 'active' | 'inactive' | 'warning' {
  switch ((status || '').toLowerCase()) {
    case 'sentaccepted':
    case 'deliveryconfirmed':
      return 'active';
    case 'terminalfailure':
      return 'inactive';
    default:
      return 'warning';
  }
}

function canDequeueDispatch(status: string): boolean {
  switch ((status || '').toLowerCase()) {
    case 'queued':
    case 'allocated':
    case 'leased':
    case 'retryablefailure':
    case 'nogateway':
      return true;
    default:
      return false;
  }
}

function isCancelledStatus(status: string): boolean {
  return (status || '').toLowerCase() === 'cancelled';
}

export const SmsGatewayManagement: React.FC = () => {
  useDateTimePreferences();
  const [gateways, setGateways] = useState<SmsGateway[]>([]);
  const [dispatchMessages, setDispatchMessages] = useState<SmsDispatchMessage[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [scopeType, setScopeType] = useState<SmsGatewayScopeType>('Platform');
  const [tenantId, setTenantId] = useState('');
  const [region, setRegion] = useState(DEFAULT_REGION);
  const [deviceRegistrationId, setDeviceRegistrationId] = useState('');
  const [allocationTake, setAllocationTake] = useState(100);
  const [allocationRunning, setAllocationRunning] = useState(false);
  const [allocationSummary, setAllocationSummary] = useState<string | null>(null);
  const [dispatchStatusFilter, setDispatchStatusFilter] = useState('');
  const [dispatchGatewayFilter, setDispatchGatewayFilter] = useState('');
  const [dispatchTake, setDispatchTake] = useState(DISPATCH_PAGE_SIZE);
  const [dispatchTenantId, setDispatchTenantId] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [messageText, setMessageText] = useState('');
  const [dispatchRegion, setDispatchRegion] = useState(DEFAULT_REGION);
  const [dispatchPriority, setDispatchPriority] = useState(5);
  const [dispatchMaxRetries, setDispatchMaxRetries] = useState(2);
  const [dispatchCorrelationId, setDispatchCorrelationId] = useState('');
  const [dispatchPreferredGatewayId, setDispatchPreferredGatewayId] = useState('');
  const [dispatchSubmitting, setDispatchSubmitting] = useState(false);
  const [dispatchSummary, setDispatchSummary] = useState<string | null>(null);
  const [onboardingGatewayId, setOnboardingGatewayId] = useState('');
  const [onboardingApiBaseUrl, setOnboardingApiBaseUrl] = useState((import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, ''));
  const [onboardingSignalRNegotiatePath, setOnboardingSignalRNegotiatePath] = useState(DEFAULT_SIGNALR_NEGOTIATE_PATH);
  const [onboardingPayloadJson, setOnboardingPayloadJson] = useState('');
  const [onboardingQrDataUrl, setOnboardingQrDataUrl] = useState('');
  const [onboardingStatus, setOnboardingStatus] = useState<string | null>(null);
  const [onboardingBusy, setOnboardingBusy] = useState(false);

  const tenantOptions = useMemo(
    () => tenants.filter((t) => (t.slug || '').toLowerCase() !== 'system').sort((a, b) => a.name.localeCompare(b.name)),
    [tenants],
  );

  const selectedOnboardingGateway = gateways.find((gateway) => gateway.gatewayId === onboardingGatewayId) || null;
  const selectedOnboardingTenant = selectedOnboardingGateway
    ? tenants.find((tenant) => tenant.tenantId === selectedOnboardingGateway.tenantId) || null
    : null;

  const enqueueGatewayOptions = useMemo(() => {
    return gateways.filter((gateway) => {
      if (gateway.scopeType === 'Platform') {
        return true;
      }

      return !!dispatchTenantId && gateway.scopeType === 'TenantDedicated' && gateway.tenantId === dispatchTenantId;
    });
  }, [dispatchTenantId, gateways]);

  const loadGateways = async () => {
    const [gatewayList, tenantList] = await Promise.all([
      adminApi.getPlatformSmsGateways(),
      adminApi.getTenants({ skip: 0, take: 500 }),
    ]);
    setGateways(gatewayList);
    setTenants(tenantList.tenants || []);
  };

  const loadDispatchMessages = async () => {
    const messageList = await adminApi.getPlatformSmsDispatchMessages({
      take: dispatchTake,
      status: dispatchStatusFilter || undefined,
      gatewayId: dispatchGatewayFilter || undefined,
    });
    // Keep cancelled rows out of the default "All" view so dequeue behaves like removing work from queue.
    const visible = dispatchStatusFilter
      ? messageList
      : messageList.filter((message) => !isCancelledStatus(message.status));
    setDispatchMessages(visible);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadGateways(), loadDispatchMessages()]);
    } catch (err: any) {
      setError(err.message || 'Failed to load SMS gateway data');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      await Promise.all([loadGateways(), loadDispatchMessages()]);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh SMS gateway data');
    }
  };

  useEffect(() => {
    void loadData();

    const intervalId = window.setInterval(() => {
      void refreshData();
    }, AUTO_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatchStatusFilter, dispatchGatewayFilter, dispatchTake]);

  const resetForm = () => {
    setName('');
    setScopeType('Platform');
    setTenantId('');
    setRegion(DEFAULT_REGION);
    setDeviceRegistrationId('');
  };

  const clearDispatchFilters = () => {
    setDispatchStatusFilter('');
    setDispatchGatewayFilter('');
    setDispatchTake(DISPATCH_PAGE_SIZE);
  };

  const handleGenerateOnboardingQr = async () => {
    if (!selectedOnboardingGateway) {
      setOnboardingStatus('Select a gateway to generate onboarding QR payload.');
      return;
    }

    setOnboardingBusy(true);
    setOnboardingStatus(null);
    try {
      const response = await adminApi.generatePlatformSmsGatewayOnboardingPayload(selectedOnboardingGateway.gatewayId, {
        apiBaseUrl: onboardingApiBaseUrl.trim(),
        signalRNegotiatePath: onboardingSignalRNegotiatePath.trim() || DEFAULT_SIGNALR_NEGOTIATE_PATH,
      });
      const payload = response.payloadJson || '';
      setOnboardingPayloadJson(payload);
      setOnboardingQrDataUrl(await QRCode.toDataURL(payload, { errorCorrectionLevel: 'L', margin: 2, width: 420 }));
      setOnboardingStatus(`Generated onboarding QR for gateway '${selectedOnboardingGateway.name}'.`);
    } catch (err: any) {
      setOnboardingStatus(err?.message || 'Failed to generate onboarding QR payload');
    } finally {
      setOnboardingBusy(false);
    }
  };

  const handleCopyOnboardingPayload = async () => {
    if (!onboardingPayloadJson) {
      setOnboardingStatus('No onboarding payload available to copy.');
      return;
    }

    try {
      if (!navigator.clipboard?.writeText) {
        setOnboardingStatus('Clipboard API unavailable.');
        return;
      }

      await navigator.clipboard.writeText(onboardingPayloadJson);
      setOnboardingStatus('Onboarding payload copied to clipboard.');
    } catch (err: any) {
      setOnboardingStatus(err?.message || 'Failed to copy onboarding payload');
    }
  };

  const handleAllocate = async () => {
    setAllocationRunning(true);
    setAllocationSummary(null);
    setError(null);
    try {
      const result = await adminApi.allocatePlatformSmsDispatchMessages(allocationTake);
      setAllocationSummary(`Allocated ${result.allocated} of ${result.requested} requested messages.`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to allocate SMS dispatch messages');
    } finally {
      setAllocationRunning(false);
    }
  };

  const handleEnqueueDispatch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dispatchTenantId) {
      setError('Tenant is required for queue test dispatch.');
      return;
    }
    if (!recipientPhone.trim() || !messageText.trim()) {
      setError('Recipient phone and message text are required.');
      return;
    }

    const payload: CreatePlatformSmsDispatchMessageRequest = {
      tenantId: dispatchTenantId,
      recipientPhone: recipientPhone.trim(),
      messageText: messageText.trim(),
      priority: dispatchPriority,
      maxRetries: dispatchMaxRetries,
      correlationId: dispatchCorrelationId.trim() || undefined,
      region: dispatchRegion.trim() || undefined,
      preferredGatewayId: dispatchPreferredGatewayId || undefined,
    };

    setDispatchSubmitting(true);
    setDispatchSummary(null);
    setError(null);
    try {
      const created = await adminApi.enqueuePlatformSmsDispatchMessage(payload);
      setRecipientPhone('');
      setMessageText('');
      setDispatchCorrelationId('');
      setDispatchSummary(`Queued message ${created.dispatchMessageId.slice(0, 8)} with status ${created.status}.`);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to enqueue SMS dispatch message');
    } finally {
      setDispatchSubmitting(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Gateway name is required.');
      return;
    }
    if (!region.trim()) {
      setError('Region is required.');
      return;
    }
    if (scopeType === 'TenantDedicated' && !tenantId) {
      setError('Tenant is required for tenant-dedicated gateways.');
      return;
    }

    const payload: CreateSmsGatewayRequest = {
      name: name.trim(),
      scopeType,
      tenantId: scopeType === 'TenantDedicated' ? tenantId : null,
      region: region.trim(),
      deviceRegistrationId: deviceRegistrationId.trim() || undefined,
      isEnabled: true,
    };

    setSubmitting(true);
    setError(null);
    try {
      await adminApi.createPlatformSmsGateway(payload);
      resetForm();
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to create SMS gateway');
    } finally {
      setSubmitting(false);
    }
  };

  const updateGateway = async (gatewayId: string, data: UpdateSmsGatewayRequest) => {
    setError(null);
    try {
      await adminApi.updatePlatformSmsGateway(gatewayId, data);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to update SMS gateway');
    }
  };

  const handleDelete = async (gateway: SmsGateway) => {
    if (!window.confirm(`Delete SMS gateway '${gateway.name}'?`)) {
      return;
    }
    setError(null);
    try {
      await adminApi.deletePlatformSmsGateway(gateway.gatewayId);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete SMS gateway');
    }
  };

  const handleTriggerCollect = async (gateway: SmsGateway) => {
    setError(null);
    try {
      await adminApi.triggerPlatformSmsGatewayCollect(gateway.gatewayId);
      alert('Collect trigger sent successfully.');
    } catch (err: any) {
      setError(err.message || 'Failed to trigger SMS collect');
    }
  };

  const handleDequeueDispatch = async (message: SmsDispatchMessage) => {
    if (!window.confirm(`Dequeue message ${message.dispatchMessageId.slice(0, 8)} for ${message.recipientPhone}?`)) {
      return;
    }

    setError(null);
    setAllocationSummary(null);
    try {
      const updated = await adminApi.dequeuePlatformSmsDispatchMessage(message.dispatchMessageId);
      setDispatchMessages((current) => current.filter((item) => item.dispatchMessageId !== updated.dispatchMessageId));
      setAllocationSummary(`Dequeued message ${updated.dispatchMessageId.slice(0, 8)} and removed it from the active queue view.`);
    } catch (err: any) {
      setError(err.message || 'Failed to dequeue SMS dispatch message');
    }
  };

  return (
    <div className="management-section">
      <div className="section-header">
        <h2>SMS Gateways</h2>
      </div>

      <p className="help-text" style={{ marginBottom: '1rem' }}>
        Register platform or tenant-dedicated Android SMS gateways. Tenant-dedicated gateways are isolated to one tenant.
      </p>

      {error && <div className="error-message">{error}</div>}

      <div className="section-header" style={{ marginTop: '0.5rem' }}>
        <h3>Gateway Onboarding QR</h3>
      </div>

      <p className="help-text" style={{ marginBottom: '1rem' }}>
        Generate onboarding QR payloads for platform or tenant-dedicated gateway devices via backend onboarding endpoints. This is additive and does not alter existing gateway governance workflows.
      </p>

      <div className="settings-form" style={{ marginBottom: '1rem' }}>
        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.4fr 1.2fr auto auto', gap: '0.75rem' }}>
          <div className="form-group">
            <label>Gateway</label>
            <select value={onboardingGatewayId} onChange={(e) => setOnboardingGatewayId(e.target.value)} disabled={gateways.length === 0}>
              <option value="">Select a gateway</option>
              {gateways.map((gateway) => {
                const tenant = tenants.find((item) => item.tenantId === gateway.tenantId);
                const tenantLabel = tenant ? ` / ${tenant.name}` : '';
                return (
                  <option key={gateway.gatewayId} value={gateway.gatewayId}>
                    {gateway.name} ({gateway.scopeType}{tenantLabel}, {gateway.region})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="form-group">
            <label>API base URL</label>
            <input value={onboardingApiBaseUrl} onChange={(e) => setOnboardingApiBaseUrl(e.target.value)} placeholder="https://api.example.com" />
          </div>

          <div className="form-group">
            <label>SignalR negotiate path</label>
            <input value={onboardingSignalRNegotiatePath} onChange={(e) => setOnboardingSignalRNegotiatePath(e.target.value)} placeholder={DEFAULT_SIGNALR_NEGOTIATE_PATH} />
          </div>

          <div className="form-group" style={{ alignSelf: 'end' }}>
            <button type="button" className="btn-primary" onClick={() => void handleGenerateOnboardingQr()} disabled={onboardingBusy || gateways.length === 0}>
              {onboardingBusy ? 'Generating...' : 'Generate QR'}
            </button>
          </div>

          <div className="form-group" style={{ alignSelf: 'end' }}>
            <button type="button" className="btn-secondary" onClick={() => void handleCopyOnboardingPayload()} disabled={!onboardingPayloadJson}>
              Copy payload
            </button>
          </div>
        </div>

        {selectedOnboardingGateway && (
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.2fr 1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
            <div className="form-group">
              <label>Gateway ID</label>
              <input value={selectedOnboardingGateway.gatewayId} readOnly />
            </div>
            <div className="form-group">
              <label>Scope</label>
              <input value={selectedOnboardingGateway.scopeType} readOnly />
            </div>
            <div className="form-group">
              <label>Tenant</label>
              <input value={selectedOnboardingTenant ? `${selectedOnboardingTenant.name} (${selectedOnboardingTenant.slug})` : '-'} readOnly />
            </div>
            <div className="form-group">
              <label>Region</label>
              <input value={selectedOnboardingGateway.region} readOnly />
            </div>
            <div className="form-group">
              <label>Device ID</label>
              <input value={selectedOnboardingGateway.deviceRegistrationId || ''} readOnly placeholder="optional" />
            </div>
          </div>
        )}

        {onboardingStatus ? <p className="help-text" style={{ marginTop: '0.75rem' }}>{onboardingStatus}</p> : null}

        <div
          className="form-row"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(340px, 380px) minmax(0, 1fr)',
            gap: '1rem',
            marginTop: '0.75rem',
            alignItems: 'start',
          }}>
          <div className="form-group">
            <label>Gateway onboarding QR</label>
            <div className="mobile-enrollment-qr-wrap">
              {onboardingQrDataUrl ? <img src={onboardingQrDataUrl} alt="Gateway onboarding QR" className="mobile-enrollment-qr" /> : <span className="table-info">Generate payload to render QR</span>}
            </div>
          </div>
          <div className="form-group">
            <label>Payload JSON</label>
            <textarea value={onboardingPayloadJson} onChange={() => undefined} rows={10} />
          </div>
        </div>
      </div>

      <form className="settings-form" onSubmit={handleCreate} style={{ marginBottom: '1rem' }}>
        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr 1fr auto', gap: '0.75rem' }}>
          <div className="form-group">
            <label>Gateway Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., AU-SYD-01" required />
          </div>

          <div className="form-group">
            <label>Scope</label>
            <select value={scopeType} onChange={(e) => setScopeType(e.target.value as SmsGatewayScopeType)}>
              <option value="Platform">Platform</option>
              <option value="TenantDedicated">Tenant Dedicated</option>
            </select>
          </div>

          <div className="form-group">
            <label>Region</label>
            <input value={region} onChange={(e) => setRegion(e.target.value)} required />
          </div>

          <div className="form-group">
            <label>Tenant (if dedicated)</label>
            <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={scopeType !== 'TenantDedicated'}>
              <option value="">Select tenant</option>
              {tenantOptions.map((tenant) => (
                <option key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.name} ({tenant.slug})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Device ID</label>
            <input value={deviceRegistrationId} onChange={(e) => setDeviceRegistrationId(e.target.value)} placeholder="optional" />
          </div>

          <div className="form-group" style={{ alignSelf: 'end' }}>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Add'}
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <div className="loading">Loading SMS gateways...</div>
      ) : (
        <>
          <table className="data-table" style={{ marginBottom: '1rem' }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Scope</th>
                <th>Tenant</th>
                <th>Region</th>
                <th>Status</th>
                <th>Last Seen</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {gateways.map((gateway) => {
                const tenant = tenants.find((t) => t.tenantId === gateway.tenantId);
                const status = (gateway.status || 'Offline').toLowerCase();
                const canTrigger = gateway.isEnabled && status === 'online' && !!gateway.connectionId;

                return (
                  <tr key={gateway.gatewayId} style={{ opacity: gateway.isEnabled ? 1 : 0.6 }}>
                    <td>{gateway.name}</td>
                    <td>{gateway.scopeType}</td>
                    <td>{tenant ? tenant.name : gateway.tenantId || '-'}</td>
                    <td>{gateway.region}</td>
                    <td>
                      <span className={`status-badge ${status === 'online' ? 'active' : status === 'inactive' ? 'inactive' : 'warning'}`}>
                        {gateway.status || 'Offline'}
                      </span>
                    </td>
                    <td>{gateway.lastSeenAt ? formatDateTime(gateway.lastSeenAt) : '-'}</td>
                    <td>
                      <button
                        className="btn-icon"
                        title={gateway.isEnabled ? 'Disable' : 'Enable'}
                        onClick={() => updateGateway(gateway.gatewayId, { isEnabled: !gateway.isEnabled, status: gateway.isEnabled ? 'Inactive' : 'Offline' })}
                      >
                        {gateway.isEnabled ? '⏸️' : '▶️'}
                      </button>
                      <button
                        className="btn-icon"
                        title="Trigger collect"
                        disabled={!canTrigger}
                        onClick={() => handleTriggerCollect(gateway)}
                      >
                        📡
                      </button>
                      <button className="btn-icon" title="Delete" onClick={() => handleDelete(gateway)}>
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
              {gateways.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-row">No SMS gateways registered.</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="section-header" style={{ marginTop: '0.5rem' }}>
            <h3>Recent Dispatch Activity</h3>
          </div>
          <div className="section-header" style={{ marginTop: '0.5rem' }}>
            <h3>Queue Test Dispatch</h3>
          </div>
          <form className="settings-form" onSubmit={handleEnqueueDispatch} style={{ marginBottom: '1rem' }}>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 1.8fr 1fr 1fr 1.4fr 1.4fr auto', gap: '0.75rem' }}>
              <div className="form-group">
                <label>Tenant</label>
                <select
                  value={dispatchTenantId}
                  onChange={(e) => {
                    const nextTenantId = e.target.value;
                    setDispatchTenantId(nextTenantId);
                    setDispatchPreferredGatewayId('');
                  }}
                  required
                >
                  <option value="">Select tenant</option>
                  {tenantOptions.map((tenant) => (
                    <option key={tenant.tenantId} value={tenant.tenantId}>
                      {tenant.name} ({tenant.slug})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Recipient Phone</label>
                <input value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} placeholder="e.g., +61123456789" required />
              </div>
              <div className="form-group">
                <label>Message</label>
                <input value={messageText} onChange={(e) => setMessageText(e.target.value)} placeholder="Test SMS message" required />
              </div>
              <div className="form-group">
                <label>Region</label>
                <input value={dispatchRegion} onChange={(e) => setDispatchRegion(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Priority</label>
                <input type="number" min={0} max={9} value={dispatchPriority} onChange={(e) => setDispatchPriority(Math.max(0, Math.min(9, Number(e.target.value) || 0)))} />
              </div>
              <div className="form-group">
                <label>Correlation / Retries</label>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem' }}>
                  <input value={dispatchCorrelationId} onChange={(e) => setDispatchCorrelationId(e.target.value)} placeholder="optional" />
                  <input type="number" min={0} max={10} value={dispatchMaxRetries} onChange={(e) => setDispatchMaxRetries(Math.max(0, Math.min(10, Number(e.target.value) || 0)))} />
                </div>
              </div>
              <div className="form-group">
                <label>Preferred Gateway</label>
                <select value={dispatchPreferredGatewayId} onChange={(e) => setDispatchPreferredGatewayId(e.target.value)}>
                  <option value="">Auto-select</option>
                  {enqueueGatewayOptions.map((gateway) => (
                    <option key={gateway.gatewayId} value={gateway.gatewayId}>
                      {gateway.name} ({gateway.scopeType}, {gateway.status || 'Offline'})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ alignSelf: 'end' }}>
                <button type="submit" className="btn-primary" disabled={dispatchSubmitting}>
                  {dispatchSubmitting ? 'Queueing...' : 'Queue'}
                </button>
              </div>
            </div>
          </form>
          {dispatchSummary && <p className="help-text" style={{ marginBottom: '1rem' }}>{dispatchSummary}</p>}
          <div className="filters" style={{ marginBottom: '0.75rem', alignItems: 'center' }}>
            <input
              type="number"
              min={1}
              max={500}
              value={allocationTake}
              onChange={(e) => setAllocationTake(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
              style={{ maxWidth: '8rem' }}
            />
            <button className="btn-primary" type="button" onClick={handleAllocate} disabled={allocationRunning}>
              {allocationRunning ? 'Allocating...' : 'Run Allocation'}
            </button>
            {allocationSummary && <span className="help-text">{allocationSummary}</span>}
          </div>
          <div className="filters" style={{ marginBottom: '0.75rem' }}>
            <select
              value={dispatchStatusFilter}
              onChange={(e) => {
                setDispatchStatusFilter(e.target.value);
                setDispatchTake(DISPATCH_PAGE_SIZE);
              }}
            >
              <option value="">All Statuses</option>
              <option value="Queued">Queued</option>
              <option value="Allocated">Allocated</option>
              <option value="Leased">Leased</option>
              <option value="SentAccepted">Sent Accepted</option>
              <option value="DeliveryConfirmed">Delivery Confirmed</option>
              <option value="RetryableFailure">Retryable Failure</option>
              <option value="TerminalFailure">Terminal Failure</option>
              <option value="NoGateway">No Gateway</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <select
              value={dispatchGatewayFilter}
              onChange={(e) => {
                setDispatchGatewayFilter(e.target.value);
                setDispatchTake(DISPATCH_PAGE_SIZE);
              }}
            >
              <option value="">All Gateways</option>
              {gateways.map((gateway) => (
                <option key={gateway.gatewayId} value={gateway.gatewayId}>
                  {gateway.name}
                </option>
              ))}
            </select>
            <button className="btn-secondary" type="button" onClick={clearDispatchFilters}>
              Clear
            </button>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Tenant</th>
                <th>Recipient</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Gateway</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dispatchMessages.map((message) => {
                const tenant = tenants.find((t) => t.tenantId === message.tenantId);
                const tone = getDispatchStatusTone(message.status);
                const gateway = gateways.find((item) => item.gatewayId === message.allocatedGatewayId);
                const canDequeue = canDequeueDispatch(message.status);
                return (
                  <tr key={message.dispatchMessageId}>
                    <td>{formatDateTime(message.createdAt)}</td>
                    <td>{tenant ? tenant.name : message.tenantId}</td>
                    <td>{message.recipientPhone}</td>
                    <td>
                      <span className={`status-badge ${tone}`}>
                        {message.status}
                      </span>
                    </td>
                    <td>{message.attemptCount}/{message.maxRetries}</td>
                    <td>{gateway?.name || message.allocatedGatewayId || '-'}</td>
                    <td>
                      {canDequeue ? (
                        <button
                          className="btn-secondary"
                          type="button"
                          onClick={() => void handleDequeueDispatch(message)}
                          title="Cancel this dispatch before it is finalized"
                        >
                          Dequeue
                        </button>
                      ) : (
                        <span style={{ color: '#64748b' }}>-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {dispatchMessages.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-row">No dispatch messages found.</td>
                </tr>
              )}
            </tbody>
          </table>
          {dispatchMessages.length >= dispatchTake && (
            <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" type="button" onClick={() => setDispatchTake((current) => current + DISPATCH_PAGE_SIZE)}>
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SmsGatewayManagement;
